/**
 * Number formatting for the Financial section.
 *
 * Case-study economics are stored as integer cents; calculator outputs are
 * plain dollars. These helpers are the only place either becomes a string, so
 * rounding behaviour stays consistent across cards, tables, and readouts.
 *
 * Ported verbatim from Realiform's `src/lib/format.ts` apart from
 * `humanizeSlug`, which the explicit label maps in `types.ts` replace.
 */

const usd = (opts: Intl.NumberFormatOptions = {}) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', ...opts });

const wholeUsd = usd({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
const centsUsd = usd({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** $1,250 — the default for headline figures. */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return wholeUsd.format(cents / 100);
}

/** $1,250.00 — when the cents genuinely matter (unit economics, per-unit costs). */
export function formatCentsExact(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return centsUsd.format(cents / 100);
}

/** $12.4K / $1.2M — for dense cards where full figures would wrap. */
export function formatCentsCompact(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const dollars = cents / 100;
  if (Math.abs(dollars) < 10_000) return wholeUsd.format(dollars);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(dollars);
}

/** Dollars (not cents) in, formatted out. For calculator outputs. */
export function formatDollars(
  dollars: number | null | undefined,
  fractionDigits = 0
): string {
  if (dollars == null || !Number.isFinite(dollars)) return '—';
  return usd({
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(dollars);
}

/** 0.184 → "18.4%" */
export function formatRatioAsPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** 18.4 → "18.4%" — when the value is already in percentage points. */
export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** 1.8 → "1.8×" */
export function formatMultiple(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}×`;
}

/** 14 → "1 yr 2 mo"; keeps break-even readouts human. */
export function formatMonths(months: number | null | undefined): string {
  if (months == null || !Number.isFinite(months) || months < 0) return '—';
  if (months < 1) return '<1 mo';
  const whole = Math.round(months);
  if (whole < 12) return `${whole} mo`;
  const years = Math.floor(whole / 12);
  const rem = whole % 12;
  return rem === 0 ? `${years} yr` : `${years} yr ${rem} mo`;
}

/**
 * "14 Jun 2026" from an ISO date string.
 *
 * Parsed as UTC and formatted in UTC. A bare `new Date('2026-06-14')` is
 * already UTC midnight, which renders as the previous day for any reader west
 * of Greenwich unless the timezone is pinned here too.
 */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}
